<template>
  <Teleport to="body">
    <div class="onboarding" role="dialog" aria-modal="true" aria-label="绘本创作新手引导">
      <div
        class="onboarding__shade"
        :class="{ 'onboarding__shade--visible': activeIndex === 0 }"
        aria-hidden="true"
      ></div>
      <div
        v-if="spotlightStyle"
        class="onboarding__spotlight"
        :class="{
          'onboarding__spotlight--character': activeIndex === 1,
          'onboarding__spotlight--topic': activeIndex === 2,
          'onboarding__spotlight--generate': activeIndex === 3
        }"
        :style="spotlightStyle"
      ></div>
      <div
        v-if="activeIndex === 1 && targetHintStyle"
        class="onboarding__target-hint"
        :style="targetHintStyle"
      >
        选择角色
      </div>

      <section
        ref="cardRef"
        class="onboarding__card"
        :class="{
          'onboarding__card--welcome': activeIndex === 0,
          'onboarding__card--character': activeIndex === 1,
          'onboarding__card--topic': activeIndex === 2,
          'onboarding__card--generate': activeIndex === 3
        }"
        :style="cardStyle"
      >
        <header class="onboarding__header">
          <span>{{ activeIndex + 1 }}/{{ steps.length }}</span>
          <h2>{{ currentStep.title }}</h2>
          <button class="onboarding__close" type="button" aria-label="关闭" @click="finish('skip')">
            ×
          </button>
        </header>

        <template v-if="activeIndex === 0">
          <img
            class="onboarding__welcome-image"
            :src="welcomePreviewImage"
            alt="大象绘本与角色配音示例"
          />
          <p class="onboarding__welcome-copy">一起跟着教程创作第一本专属绘本吧！</p>
        </template>
        <template v-else-if="activeIndex === 1">
          <img
            class="onboarding__character-image"
            :src="characterGuideImage"
            alt="创建并选择绘本角色示例"
          />
          <p class="onboarding__character-copy">
            点击@创建自己的第一个角色，选中添加至输入框，让 TA 成为故事主人公
          </p>
        </template>
        <template v-else-if="activeIndex === 2">
          <div class="onboarding__topic-method">
            <p>方式一：角色优先,让AI围绕这些角色生成故事绘本</p>
            <img :src="topicCharacterImage" alt="引用角色输入主题示例" />
          </div>
          <div class="onboarding__topic-method">
            <p>方式二：主题优先,让AI帮你设计角色和创作剧情</p>
            <img :src="topicCustomImage" alt="直接输入故事主题示例" />
          </div>
        </template>
        <template v-else-if="activeIndex === 3">
          <img
            class="onboarding__generate-image"
            :src="generateGuideImage"
            alt="AI绘本生成结果示例"
          />
          <p class="onboarding__generate-copy">
            点击立即创作，自动撰写故事脚本、绘制全本插画与配音，约2-3分钟出稿
          </p>
        </template>
        <template v-else>
          <div class="onboarding__step-icon" aria-hidden="true">{{ currentStep.icon }}</div>
          <p class="onboarding__description">{{ currentStep.description }}</p>
        </template>

        <div class="onboarding__footer">
          <button class="onboarding__footer-skip" type="button" @click="finish('skip')">
            跳过
          </button>
          <div class="onboarding__actions">
            <button
              v-if="activeIndex > 0"
              class="onboarding__button onboarding__button--secondary"
              type="button"
              @click="previousOrRestart"
            >
              {{ activeIndex === 3 ? '重新开始' : '上一步' }}
            </button>
            <div class="onboarding__next-wrap">
              <span v-if="activeIndex === 0" class="onboarding__key-tip">按空格键继续</span>
              <button class="onboarding__button" type="button" @click="next">
                {{ isLastStep ? '开始创作' : '下一步' }}
              </button>
            </div>
          </div>
        </div>
      </section>
    </div>
  </Teleport>
</template>

<script setup lang="ts">
  import { computed, nextTick, onBeforeUnmount, onMounted, ref } from 'vue';
  import welcomePreviewImage from '@/assets/images/create-onboarding-welcome.webp';
  import characterGuideImage from '@/assets/images/create-onboarding-character.png';
  import topicCharacterImage from '@/assets/images/create-onboarding-topic-character.png';
  import topicCustomImage from '@/assets/images/create-onboarding-topic-custom.png';
  import generateGuideImage from '@/assets/images/create-onboarding-generate.webp';

  const emit = defineEmits<{
    close: [reason: 'skip' | 'complete'];
  }>();

  const steps = [
    {
      target: '',
      icon: '✦',
      title: '欢迎来到AI绘本',
      description: ''
    },
    {
      target: '.mention-btn',
      icon: '@',
      title: '选择角色形象',
      description: '点击“选择角色”，创建角色形象和设定。创作时引用角色，画面会更连贯。'
    },
    {
      target: '.prompt-card',
      icon: '✦',
      title: '输入主题',
      description: '简单描述主角、场景和故事走向，也可以直接选用灵感示例开始创作。'
    },
    {
      target: '.generate-btn',
      icon: '✓',
      title: '生成绘本',
      description: '确认年龄、页数和画风后开始创作，之后还能继续调整故事内容与插图。'
    }
  ] as const;

  const activeIndex = ref(0);
  const targetRect = ref<DOMRect>();
  const cardRef = ref<HTMLElement>();
  const cardPosition = ref({ top: 0, left: 0 });
  let positionTimer: number | undefined;
  const currentStep = computed(() => steps[activeIndex.value]);
  const isLastStep = computed(() => activeIndex.value === steps.length - 1);
  const spotlightStyle = computed(() => {
    const rect = targetRect.value;
    if (!rect) return undefined;
    const padding = 8;
    return {
      top: `${rect.top - padding}px`,
      left: `${rect.left - padding}px`,
      width: `${rect.width + padding * 2}px`,
      height: `${rect.height + padding * 2}px`
    };
  });
  const targetHintStyle = computed(() => {
    const rect = targetRect.value;
    if (!rect) return undefined;
    const hintWidth = 76;
    return {
      top: `${rect.top + rect.height / 2 - 15}px`,
      left: `${Math.min(rect.right + 14, window.innerWidth - hintWidth - 8)}px`
    };
  });
  const cardStyle = computed(() => {
    if (activeIndex.value === 0) {
      return {
        top: '50%',
        right: 'auto',
        bottom: 'auto',
        left: '50%',
        transform: 'translate(-50%, -50%)'
      };
    }

    const rect = targetRect.value;
    if (activeIndex.value === 2 && window.innerWidth <= 600 && rect) {
      const inputOverlap = 110;
      const top = rect.bottom - inputOverlap;
      return {
        top: `${top}px`,
        right: 'auto',
        bottom: 'auto',
        left: '50%',
        maxHeight: `${Math.max(200, window.innerHeight - top - 16)}px`,
        transform: 'translateX(-50%)'
      };
    }

    if (activeIndex.value === 3 && window.innerWidth <= 600 && rect) {
      return {
        top: 'auto',
        right: 'auto',
        bottom: `${Math.max(16, window.innerHeight - rect.top + 16)}px`,
        left: '50%',
        maxHeight: 'calc(100dvh - 32px)',
        transform: 'translateX(-50%)'
      };
    }

    return {
      top: `${cardPosition.value.top}px`,
      right: 'auto',
      bottom: 'auto',
      left: `${cardPosition.value.left}px`,
      maxHeight: '',
      transform: 'none'
    };
  });

  const positionGuide = async (shouldScroll = false) => {
    if (positionTimer !== undefined) window.clearTimeout(positionTimer);
    // 步骤切换时先移除旧目标，避免新步骤样式短暂套用在上一目标上。
    if (shouldScroll) targetRect.value = undefined;
    const target = currentStep.value.target
      ? document.querySelector<HTMLElement>(currentStep.value.target)
      : null;
    const isMobile = window.innerWidth <= 600;
    if (shouldScroll) {
      target?.scrollIntoView({
        behavior: 'smooth',
        block: isMobile && activeIndex.value < 3 ? 'start' : 'center'
      });
    }
    await nextTick();
    positionTimer = window.setTimeout(
      () => {
        const rect = target?.getBoundingClientRect();
        targetRect.value = rect;
        const preferredCardWidth = 300;
        const cardWidth = Math.min(preferredCardWidth, window.innerWidth - 32);
        const cardHeight = cardRef.value?.offsetHeight ?? 300;
        const gap = 20;
        let left = rect
          ? rect.left + rect.width / 2 - cardWidth / 2
          : (window.innerWidth - cardWidth) / 2;
        let top = rect ? rect.bottom + gap : (window.innerHeight - cardHeight) / 2;

        if (activeIndex.value === 1 && rect) {
          left = rect.right + 6;
          top = rect.bottom + 18;
        }
        if (activeIndex.value === 2 && rect) {
          left = rect.right - cardWidth - 38;
          top = rect.top + 58;
        }
        if (activeIndex.value === 3 && rect) {
          left = rect.right - cardWidth + 50;
          top = rect.top - 102;
        }

        if (isMobile && activeIndex.value > 0 && rect) {
          left = (window.innerWidth - cardWidth) / 2;
          top = activeIndex.value === 3 ? rect.top - cardHeight - 16 : rect.bottom + 16;
        }

        left = Math.max(16, Math.min(left, window.innerWidth - cardWidth - 16));
        if (top + cardHeight > window.innerHeight - 16 && rect) top = rect.top - cardHeight - gap;
        top = Math.max(16, Math.min(top, window.innerHeight - cardHeight - 16));
        cardPosition.value = { top, left };
        positionTimer = undefined;
      },
      shouldScroll ? 280 : 0
    );
  };

  const previousOrRestart = () => {
    activeIndex.value = activeIndex.value === 3 ? 0 : activeIndex.value - 1;
    void positionGuide(true);
  };

  const next = () => {
    if (isLastStep.value) {
      finish('complete');
      return;
    }
    activeIndex.value += 1;
    void positionGuide(true);
  };

  const finish = (reason: 'skip' | 'complete') => emit('close', reason);
  const handleKeydown = (event: KeyboardEvent) => {
    if (event.key === 'Escape') finish('skip');
    if (event.code === 'Space') {
      event.preventDefault();
      next();
    }
  };
  const handleViewportChange = () => void positionGuide();

  onMounted(() => {
    document.addEventListener('keydown', handleKeydown);
    window.addEventListener('resize', handleViewportChange);
    window.addEventListener('scroll', handleViewportChange, true);
    void positionGuide();
  });

  onBeforeUnmount(() => {
    if (positionTimer !== undefined) window.clearTimeout(positionTimer);
    document.removeEventListener('keydown', handleKeydown);
    window.removeEventListener('resize', handleViewportChange);
    window.removeEventListener('scroll', handleViewportChange, true);
  });
</script>

<style scoped>
  .onboarding {
    position: fixed;
    inset: 0;
    z-index: 4000;
    color: #25222d;
  }
  .onboarding__shade {
    display: none;
    position: absolute;
    inset: 0;
    background: rgba(31, 28, 40, 0.6);
  }
  .onboarding__shade--visible {
    display: block;
  }
  .onboarding__spotlight {
    position: fixed;
    z-index: 1;
    border-radius: 12px;
    box-shadow:
      0 0 0 3px #fff,
      0 0 0 9999px rgba(31, 28, 40, 0.6);
    pointer-events: none;
    transition: all 0.25s ease;
  }
  .onboarding__spotlight--character {
    border-radius: 50%;
    box-shadow:
      0 0 0 2px #6033e1,
      0 0 0 9999px rgba(31, 28, 40, 0.6);
  }
  .onboarding__spotlight--character::after,
  .onboarding__spotlight--topic::after,
  .onboarding__spotlight--generate::after {
    position: absolute;
    width: 12px;
    height: 12px;
    border-radius: 50%;
    background: #ee5526;
    box-shadow: 0 0 0 5px rgba(255, 97, 48, 0.5);
    content: '';
    transform-origin: center;
    animation: onboarding-interaction-point-pulse 1s ease-in-out infinite;
    animation-play-state: running;
    will-change: transform, box-shadow, filter, opacity;
  }
  .onboarding__spotlight--character::after {
    right: -3px;
    bottom: 0;
  }
  @keyframes onboarding-interaction-point-pulse {
    0%,
    100% {
      box-shadow: 0 0 0 5px rgba(255, 97, 48, 0.38);
      filter: brightness(1);
      opacity: 0.86;
      transform: scale(0.94);
    }
    50% {
      box-shadow: 0 0 0 5px rgba(255, 97, 48, 0.58);
      filter: brightness(1.08);
      opacity: 1;
      transform: scale(1.06);
    }
  }
  .onboarding__spotlight--topic {
    box-shadow:
      0 0 0 2px #6033e1,
      0 0 0 9999px rgba(31, 28, 40, 0.6);
  }
  .onboarding__spotlight--topic::after {
    top: 48px;
    left: 52%;
  }
  .onboarding__spotlight--generate {
    box-shadow:
      0 0 0 2px #6033e1,
      0 0 0 9999px rgba(31, 28, 40, 0.6);
  }
  .onboarding__spotlight--generate::after {
    top: calc(50% - 6px);
    left: calc(50% + 100px);
  }
  .onboarding__target-hint {
    position: fixed;
    z-index: 3;
    height: 30px;
    padding: 0 10px;
    border-radius: 5px;
    background: #151515;
    color: #fff;
    font-size: 13px;
    line-height: 30px;
    pointer-events: none;
  }
  .onboarding__card {
    position: fixed;
    z-index: 2;
    box-sizing: border-box;
    width: min(300px, calc(100vw - 32px));
    overflow: visible;
    border: 1px solid #e7e4eb;
    border-radius: 8px;
    background: #fff;
    box-shadow: 0 14px 44px rgba(25, 20, 43, 0.25);
    transition:
      top 0.25s ease,
      left 0.25s ease;
  }
  .onboarding__card--welcome {
    width: min(300px, calc(100vw - 32px));
  }
  .onboarding__card--character {
    width: min(300px, calc(100vw - 32px));
  }
  .onboarding__card--topic {
    width: min(300px, calc(100vw - 32px));
  }
  .onboarding__card--generate {
    width: min(300px, calc(100vw - 32px));
  }
  .onboarding__header {
    display: flex;
    height: 48px;
    align-items: center;
    gap: 8px;
    box-sizing: border-box;
    padding: 0 12px;
    border-bottom: 1px solid #eceaf0;
  }
  .onboarding__header > span {
    flex: none;
    color: #66616d;
    font-size: 13px;
    font-weight: 500;
  }
  .onboarding__header h2 {
    min-width: 0;
    flex: 1;
    margin: 0;
    font-size: 14px;
    font-weight: 500;
    letter-spacing: 0;
  }
  .onboarding__close {
    display: grid;
    width: 28px;
    height: 28px;
    flex: none;
    place-items: center;
    border: 0;
    background: transparent;
    color: #66616d;
    font-size: 20px;
    line-height: 1;
    cursor: pointer;
  }
  .onboarding__welcome-image {
    display: block;
    width: calc(100% - 24px);
    height: auto;
    margin: 12px;
  }
  .onboarding__welcome-copy {
    margin: 0;
    padding: 0 12px 10px;
    color: #48434f;
    font-size: 14px;
  }
  .onboarding__character-image {
    display: block;
    width: calc(100% - 32px);
    height: auto;
    margin: 12px 16px;
  }
  .onboarding__character-copy {
    margin: 0 16px 12px;
    padding-bottom: 12px;
    border-bottom: 1px solid #eceaf0;
    color: #38343e;
    font-size: 14px;
    line-height: 1.55;
  }
  .onboarding__topic-method {
    margin: 0 16px;
  }
  .onboarding__topic-method:first-of-type {
    margin-top: 10px;
  }
  .onboarding__topic-method p {
    margin: 0 0 8px;
    color: #38343e;
    font-size: 14px;
    line-height: 1.5;
  }
  .onboarding__topic-method img {
    display: block;
    width: 100%;
    height: auto;
    margin-bottom: 12px;
  }
  .onboarding__generate-image {
    display: block;
    width: calc(100% - 24px);
    height: auto;
    margin: 10px 12px;
  }
  .onboarding__generate-copy {
    margin: 0 12px 10px;
    color: #38343e;
    font-size: 14px;
    line-height: 1.6;
  }
  .onboarding__step-icon {
    display: grid;
    width: 48px;
    height: 48px;
    place-items: center;
    margin: 24px 28px 18px;
    border-radius: 50%;
    background: #f0ecff;
    color: #7558eb;
    font-size: 24px;
    font-weight: 700;
  }
  .onboarding__description {
    margin: 12px 28px 26px;
    color: #696572;
    font-size: 14px;
    line-height: 1.75;
  }
  .onboarding__footer,
  .onboarding__actions {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
  }
  .onboarding__footer {
    min-height: 48px;
    box-sizing: border-box;
    padding: 6px 12px;
    border-top: 1px solid #eceaf0;
  }
  .onboarding__footer-skip {
    border: 0;
    background: transparent;
    color: #aaa5ae;
    font-size: 12px;
    cursor: pointer;
  }
  .onboarding__next-wrap {
    position: relative;
    z-index: 2;
  }
  .onboarding__key-tip {
    position: absolute;
    z-index: 3;
    top: calc(100% + 7px);
    right: -22px;
    width: max-content;
    padding: 5px 9px;
    border-radius: 4px;
    background: #25222a;
    color: #fff;
    font-size: 10px;
    pointer-events: none;
  }
  .onboarding__key-tip::after {
    position: absolute;
    right: 29px;
    bottom: 100%;
    border: 4px solid transparent;
    border-bottom-color: #25222a;
    content: '';
  }
  .onboarding__button {
    min-width: 68px;
    height: 32px;
    padding: 0 14px;
    border: 0;
    border-radius: 4px;
    background: #1677ff;
    color: #fff;
    font-size: 13px;
    cursor: pointer;
  }
  .onboarding__button:hover {
    background: #096dd9;
  }
  .onboarding__button--secondary {
    min-width: auto;
    padding: 0 8px;
    background: transparent;
    color: #5d5866;
  }
  .onboarding__button--secondary:hover {
    background: transparent;
    color: #5d5866;
  }
  @media (max-width: 600px) {
    .onboarding__key-tip {
      display: none;
    }

    .onboarding__card {
      max-height: calc(100dvh - 32px);
      overflow-y: auto;
    }
    .onboarding__card--welcome {
      overflow: visible;
    }
  }
</style>
